import { assign, DoneInvokeEvent } from "xstate";
import {
  ContactSelectors,
  ParentContextWithWallet,
  ParentState,
  PriceChangeType,
} from "@capone/checkout";
import {
  TravelWalletCredit,
  Transaction,
  ActivityPriceQuote,
} from "redmond";
import { Product, QuoteSuccess } from "@b2bportal/purchase-api";

import { ActivityMachineContext } from "../types";
import { SetInitialBookTimeEvent, SetPlatformEvent } from "../events";
import { getOpaquePayments } from "../selectors/common";
import {
  getBookingQuestionAnswers,
  getActivityPriceBreakdown,
  getActivityShop,
} from "../selectors/activity";
import {
  ActivityTravelerSelectors,
  ActivityAdditionalDetailsSelectors,
} from "../../../../checkout";

export const setPaymentFulfillParams = assign((ctx: ActivityMachineContext) => {
  const opaquePayments = getOpaquePayments({ context: ctx });

  ctx[ParentState.cartFulfill].fulfillRequestPayments?.push(...opaquePayments);

  return ctx;
});

export const setPrepareDetailsFulfillParams = assign(
  (context: ActivityMachineContext) => {
    const shopDetails = getActivityShop({ context });
    const priceBreakdown = getActivityPriceBreakdown({ context });
    const contactInformation = ContactSelectors.getContactInformation({
      context,
    });
    const primaryTraveler =
      ActivityTravelerSelectors.getSelectedPrimaryTraveler({ context });
    const travelers = ActivityTravelerSelectors.getAllSelectedUserTravelers({
      context,
    });
    const bookingQuestionAnswers = getBookingQuestionAnswers({ context });
    const bookableLangGuides =
      ActivityAdditionalDetailsSelectors.getBookableLangGuides({ context });
    const selectedLangGuide =
      ActivityAdditionalDetailsSelectors.getSelectedLangGuide({ context });

    if (!shopDetails || !priceBreakdown || !primaryTraveler) {
      throw new Error("Missing shop response or price breakdown");
    }

    const { id, name, languageGuides, duration, logistics } = shopDetails;
    const { startTime, date, optionId, totalPriceWithFees } = priceBreakdown;

    const productType = "Activities";

    context[ParentState.cartFulfill].fulfillPrepareDetails?.push({
      type: productType as Product,
      value: {
        languageGuide:
          selectedLangGuide || bookableLangGuides[0] || languageGuides[0],
        bookerInfo: {
          firstName: primaryTraveler.givenName,
          lastName: primaryTraveler.surname,
          personId: primaryTraveler.id,
          middleName: primaryTraveler.middleName,
          gender: primaryTraveler.gender,
          dateOfBirth: primaryTraveler.dateOfBirth,
          nationality: primaryTraveler.nationality?.country,
        },
        bookingQuestionAnswers,
        communication: {
          phone: contactInformation.phoneNumber,
          email: contactInformation.email,
        },
        experienceDetails: {
          experienceId: id,
          experienceOptionId: optionId,
          productTitle: name,
          totalPurchasePrice: totalPriceWithFees.fiat,
          category: "tour",
          startLocation: logistics.startLocations[0] || {
            // so that schedule fulfill would pass // created COTA-1965 to look into it, in the meantime we will send a mock location for start location // there are some cases where activity might be missing start location
            name: "Activity missing start location, using a mock address instead",
            address: "W 59th St &, Center Drive, New York, NY 10019, USA",
            coordinates: {
              lat: 40.76616717275202,
              lon: -73.97555915820764,
            },
          },
          startDate: date,
          startTime,
          durationMinutes: duration,
          travelers: travelers.map((traveler) => ({
            firstName: traveler.givenName,
            lastName: traveler.surname,
          })),
          numberOfTravelers: travelers.length,
        },
      },
    });

    return context;
  }
);

export const setPlatform = assign(
  (ctx: ActivityMachineContext, event: SetPlatformEvent) => {
    ctx.platform = event.platform;

    return ctx;
  }
);

export const setWalletCredits = assign(
  (context: ParentContextWithWallet, e: unknown) => {
    const event = e as DoneInvokeEvent<[TravelWalletCredit, Transaction[]]>;
    const [credits, transactions] = event.data;

    if (credits) {
      context[ParentState.wallet].credits = credits;
    }

    if (transactions) {
      context[ParentState.wallet].creditTransactionHistory = transactions;
    }

    return context;
  }
);

export const checkForPriceChange = assign(
  (context: ActivityMachineContext, event: DoneInvokeEvent<QuoteSuccess>) => {
    const priceBreakdown = getActivityPriceBreakdown({ context });
    const quoteBreakdown = event.data?.quoteBreakdown;

    if (!quoteBreakdown) return context;

    const quotedActivityProduct = quoteBreakdown.products.find(
      (product) => product.product.type === ("Experiences" as Product)
    );

    const quotedActivityProductValue = quotedActivityProduct?.product.value as
      | ActivityPriceQuote
      | undefined;

    if (!quotedActivityProductValue) return context;

    const predictedTotal = priceBreakdown?.totalPriceWithFees?.fiat.value || 0;
    const priceQuoteTotal =
      quotedActivityProductValue.breakdown.totalPriceWithFees.fiat.value;

    const difference = Math.abs(priceQuoteTotal - predictedTotal);
    const actualDifference =
      quotedActivityProductValue.priceChangeFromShop?.fiat;

    if (!actualDifference) return context;

    const hasDifference = actualDifference.value !== 0;
    const priceChange: PriceChangeType = {
      hasDifference,
      predictedTotal,
      priceQuoteTotal,
      ...(hasDifference
        ? {
            difference,
            differenceString: difference.toFixed(),
            isIncrease: actualDifference.value > 0,
          }
        : {}),
    };

    context.cartQuote.priceChange = priceChange;

    return context;
  }
);

export const setInitialBookTime = assign(
  (ctx: ActivityMachineContext, event: SetInitialBookTimeEvent) => {
    ctx.initialTime = event.initialTime;

    return ctx;
  }
);
